import { ArrowLeftIcon, Cog8ToothIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { BudgetModal } from "../../components/quickbooks/BudgetModal";
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { BudgetLoadingKeys, clearSelectedBudget, createBudgetRequest, getBudgetRequest, createBudgetTemplate, getBudgetSettings, getBudgetSource } from "../../slices/quickbooks/budgetSlice";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { arrayHasItems, getAllowedUserPermissions } from "../../helpers/common";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../consts/general";
import { ConfrimModal } from "../../components/ConfrimModal";
import { BudgetLineItemModal } from "../../components/quickbooks/BudgetLineItemModal";
import { getReportCategories } from "../../slices/quickbooks/reportCategories";
import { BudgetLineItemLoadingKeys, clearBudgetLineItems, deleteBudgetLineItemRequest, getBudgetLineItemsCards, updateBudgetLineItemRequest, upsertBudgetLineItemFormulaRequest, upsertBudgetLineItemRequest, updateMonthValueRequest } from "../../slices/quickbooks/budgetLineItemSlice";
import { formatBudgetAmount } from "../../helpers/quickbooks";
import { BudgetLineItemFormulaModal } from "../../components/quickbooks/BudgetLineItemFormulaModal";
import { BudgetLineItemEditModal } from "../../components/quickbooks/BudgetLineItemEditModal";
import { toast } from "react-toastify";
import { monthKeysFullnameEnum, monthsOptions } from "../../consts/quickbooksReports";
import { ForecastLoadingKeys, getDemandPlanningOptions, getForecastOptions, getForecastValues } from "../../slices/quickbooks/forecastSlice";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DraggableBudgetRow } from "./DraggableBudgetRow";
import { ForecastPlanningLoadingKeys, getPlannedForecastsOptions } from "../../slices/quickbooks/forecastPlanningSlice";
import { BudgetHistorySidebar } from "../../components/planning/BudgetHistorySidebar";
import { SaveBudgetTemplateModal } from "../../components/quickbooks/SaveBudgetTemplateModal";
import { BudgetSettingsSidebar } from "../../components/budget/BudgetSettingsSidebar";
import { BudgetRefreshModal } from "../../components/budget/BudgetRefreshModal";
import { BudgetPublishModal } from "../../components/budget/BudgetPublishModal";
import BudgetSheetImportModal from "../../components/budget/BudgetSheetImportModal";
import { CHILDREN_TAB_BUDGET_SHEET_IMPORT, isAdminRole } from "../../consts/management/users/users";
import {
    getDisplayedCategoryMonthValue,
    getDisplayedNetIncomeMonthValue,
    getBudgetActualsPillState,
} from "./budgetActualsDisplay";
import { buildBudgetCsv } from "./budgetCsvExport";

const formatBudgetDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime())
        ? `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`
        : '—';
};

const getClosedBudgetPeriod = (periods, year, monthIndex) => {
    const month = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    return (periods || []).find(
        (period) => period.status === 'closed' && String(period.month).slice(0, 7) === month
    ) || null;
};

export const BudgetMonthStatusBadge = ({ periods, year, monthIndex, view = "actuals" }) => {
    const closedPeriod = getClosedBudgetPeriod(periods, year, monthIndex);
    const isClosed = !!closedPeriod;
    const hasActuals = Boolean(closedPeriod?.actuals?.lines);
    const isForecastView = view === "forecast";
    const showingActuals = hasActuals && !isForecastView;
    const title = hasActuals
        ? isForecastView
            ? "Closed: QuickBooks actuals locked; showing the approved forecast"
            : `QuickBooks P&L actuals captured ${formatBudgetDate(closedPeriod.actuals.captured_at)}. Approved forecast retained.`
        : isClosed
            ? "Closed without a QuickBooks snapshot; showing locked forecast."
            : undefined;

    return (
        <span
            className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-gray-900 ${
                showingActuals
                    ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                    : isClosed && !hasActuals
                    ? "border-gray-300 bg-white"
                    : "border-emerald-200 bg-[#D0FAE5]"
            }`}
            title={title}
        >
            {showingActuals ? "Actual" : isClosed && !hasActuals ? "Closed" : "Forecast"}
        </span>
    );
};

const AddBudgetLineItemButton = ({
    onClick
}) => {
    return (
        <tr className="border-b transition-colors hover:bg-gray-100">
            <td
                colSpan={16}
                className="align-middle px-3 py-1.5"
            >
                <button
                    className="bg-transparent text-[13px] font-medium text-gray-500 hover:text-gray-700"
                    onClick={onClick}
                >
                    + Add Budget Line Item
                </button>
            </td>
        </tr>
    )
}

const amountTextClass = (value, positiveClass = "text-gray-900") =>
    Number(String(value ?? "").replace(/[$,\s]/g, "")) < 0 ? "text-red-600" : positiveClass;

const LineItemsByCategory = ({
    items,
    access,
    onDeleteClick,
    onEditFormulaClick,
    onEditLineItemClick,
    inputIsLoading,
    selectedBudget,
    categoryId,
    resetVersion,
    sourceAccounts,
    closedMonths,
    actualsByMonth,
    demandPlanLinked,
    demandPlanName,
}) => {
    const dispatch = useDispatch();
    const [inputValues, setInputValues] = useState({});
    const [orderedItems, setOrderedItems] = useState(items || []);
    const [isLocalOrdering, setIsLocalOrdering] = useState(false);
    const [isEditingCell, setIsEditingCell] = useState(false);

    useEffect(() => {
        if (isLocalOrdering) return;
        setOrderedItems(items || []);
    }, [items, isLocalOrdering]);

    useEffect(() => {
        setIsLocalOrdering(false);
        setOrderedItems(items || []);
    }, [resetVersion]); 


    const handleInputChange = (budgetLineItemId, monthKey, value) => {
        setInputValues(prev => ({
            ...prev,
            [budgetLineItemId]: {
                ...prev[budgetLineItemId],
                [monthKey]: value
            }
        }));
    };

    const handleInputBlur = async (budgetLineItemId, monthKey, value) => {
        try {
            await dispatch(updateMonthValueRequest({
                id: budgetLineItemId,
                budgetId: selectedBudget?.id,
                monthKey,
                value
            })).unwrap();
            await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
            setInputValues([]);
            toast.success(`Value updated for ${monthKeysFullnameEnum[monthKey]}`, { position: "bottom-right" })
        } catch (error) {
            console.error('Failed to update month value:', error);
            toast.success(`Failed to update month value for ${monthKeysFullnameEnum[monthKey]}`, { position: "bottom-right" })
        }
    };

    const moveRow = useCallback((dragIndex, hoverIndex) => {
        setIsLocalOrdering(true);
        setOrderedItems(prevItems => {
            const updated = [...prevItems];
            const [removed] = updated.splice(dragIndex, 1);
            updated.splice(hoverIndex, 0, removed);
            return updated;
        });
    }, []);

    const persistOrderIfChanged = useCallback(async () => {
        // Persist order within category using 'order' ascending
        const orderUpdates = (orderedItems || []).map((it, idx) => ({ id: it.id, order: idx + 1 }));
        try {
            // Allow order-only updates without changing any other fields
            await dispatch(updateBudgetLineItemRequest({
                budgetLineItemId: null,
                orderUpdates
            })).unwrap();
            await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
        } catch (e) {
            // noop; API will toast on failure elsewhere
        } finally {
            setIsLocalOrdering(false);
        }
    }, [dispatch, orderedItems, selectedBudget?.id]);

    const ItemTypes = { ROW: 'BUDGET_ROW' };

    const totalCols = 2 + 12 + 1 + (access === "edit" ? 1 : 0);
    const CategoryDropRow = () => {
        const [{ isOver, canDrop }, drop] = useDrop({
            accept: ItemTypes.ROW,
            // Only allow drops within the same category (cross-category DnD disabled)
            canDrop: (dragged) => dragged?.sourceCategoryId === categoryId,
            drop: async (dragged, monitor) => {
                if (!dragged || !monitor.canDrop()) return;
                // Same-category drop: persist the new local order only.
                await persistOrderIfChanged();
                // Ensure any local reordering state is cleared after drop
                setIsLocalOrdering(false);
            },
            collect: (monitor) => ({
                isOver: monitor.isOver({ shallow: true }),
                canDrop: monitor.canDrop()
            })
        });
        return (
            <tr ref={drop}>
                <td colSpan={totalCols} className={`p-1 ${(isOver && canDrop) ? 'bg-blue-50' : ''}`}></td>
            </tr>
        );
    };

    return (
        <>
            <CategoryDropRow />
            {orderedItems.map((budgetLineItem, index) => (
                <DraggableBudgetRow
                    key={`${budgetLineItem?.id}-${budgetLineItem?.accountNumber}-${budgetLineItem?.accountName}` || index}
                    budgetLineItem={budgetLineItem}
                    index={index}
                    access={access}
                    categoryId={categoryId}
                    moveRow={moveRow}
                    resetLocalOrdering={() => setIsLocalOrdering(false)}
                    persistOrderIfChanged={persistOrderIfChanged}
                    inputIsLoading={inputIsLoading}
                    onEditFormulaClick={onEditFormulaClick}
                    onEditLineItemClick={onEditLineItemClick}
                    onDeleteClick={onDeleteClick}
                    rollupCandidates={(items || []).filter((item) => item.accountName !== budgetLineItem.accountName)}
                    sourceAccount={(sourceAccounts || []).find(
                        (item) => String(item.id) === String(budgetLineItem?.formula?.source_account)
                    )}
                    itemInputValues={inputValues[budgetLineItem.id]}
                    handleInputChange={handleInputChange}
                    handleInputBlur={handleInputBlur}
                    closedMonths={closedMonths}
                    actualsByMonth={actualsByMonth}
                    demandPlanLinked={demandPlanLinked}
                    demandPlanName={demandPlanName}
                />
            ))}
            <CategoryDropRow />
        </>
    );
}

const Budget = ({
    access
}) => {

    const pageParams = new URLSearchParams(window?.location?.search || "");
    const budgetId = pageParams?.get("id") ? Number(pageParams?.get("id")) : null;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const budgetView = pageParams.get("view") === "forecast" ? "forecast" : "actuals";
    const { currentUser } = useSelector((state) => state.auth);
    const { childIdToAccess: userChildAccess } = getAllowedUserPermissions({
        permissions: currentUser?.permissions || [],
        isAdmin: isAdminRole(currentUser?.role),
    });
    const budgetSheetImportAccess = userChildAccess.get(CHILDREN_TAB_BUDGET_SHEET_IMPORT);

    const [hiddenByCategoryId, setHiddenByCategoryId] = useState({});
    const hideInCategoryDuringMove = async (id, sourceCategoryId, fn) => {
        setHiddenByCategoryId(prev => {
            const next = { ...(prev || {}) };
            const set = new Set(next[sourceCategoryId] || []);
            set.add(id);
            next[sourceCategoryId] = set;
            return next;
        });
        try {
            await fn?.();
        } finally {
            setHiddenByCategoryId(prev => {
                const next = { ...(prev || {}) };
                const set = new Set(next[sourceCategoryId] || []);
                set.delete(id);
                next[sourceCategoryId] = set;
                return next;
            });
        }
    };

    const computeOrderUpdates = (items, movedId, targetIndex, targetPosition) => {
        const ids = (items || []).map((it) => it.id);
        const existingIndex = ids.indexOf(movedId);
        if (existingIndex !== -1) {
            ids.splice(existingIndex, 1);
        }
        let insertAt = typeof targetIndex === "number" ? targetIndex : ids.length;
        if (typeof targetIndex === "number" && targetPosition === "after") {
            insertAt = targetIndex + 1;
        }
        if (insertAt < 0) insertAt = 0;
        if (insertAt > ids.length) insertAt = ids.length;
        const newOrder = [...ids.slice(0, insertAt), movedId, ...ids.slice(insertAt)];
        return newOrder.map((id, idx) => ({ id, order: idx + 1 }));
    };

    const [budgetModalIsOpen, setBudgetModalIsOpen] = useState(false);
    const [deleteBudgetLineItemIsOpen, setDeleteBudgetLineItemIsOpen] = useState(false);
    const [historyIsOpen, setHistoryIsOpen] = useState(false);
    const [saveTemplateModalIsOpen, setSaveTemplateModalIsOpen] = useState(false);
    const [budgetSettingsSidebarOpen, setBudgetSettingsSidebarOpen] = useState(false);
    const [budgetSettingsPanel, setBudgetSettingsPanel] = useState(null);
    const [budgetRefreshModalOpen, setBudgetRefreshModalOpen] = useState(false);
    const [budgetPublishModalOpen, setBudgetPublishModalOpen] = useState(false);
    const [budgetSheetImportOpen, setBudgetSheetImportOpen] = useState(false);
    const [budgetUnpublish, setBudgetUnpublish] = useState(false);
    const [filteredForecasts, setFilteredForecasts] = useState([]);
    const [budgetIdToDelete, setBudgetIdToDelete] = useState("");
    const [budgetLineItemData, setBudgetLineItemData] = useState(null);
    const [budgetLineItemEditData, setBudgetLineItemEditData] = useState(null);
    const [budgetLineItemFormulaData, setBudgetLineItemFormulaData] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedBudgetForecast, setSelectedBudgetForecast] = useState(null);
    const scrollContainerRef = useRef(null);

    const handleBudgetViewChange = (view) => {
        const params = new URLSearchParams(window.location.search);
        if (view === "forecast") params.set("view", "forecast");
        else params.delete("view");
        const search = params.toString();
        navigate({
            pathname: window.location.pathname,
            search: search ? `?${search}` : "",
        }, { replace: true });
    };
    const budgetTableRef = useRef(null);
    const budgetGridSpacerRef = useRef(null);
    const [budgetGridSpacerHeight, setBudgetGridSpacerHeight] = useState(0);
    const [listResetVersion, setListResetVersion] = useState(0);

    const {
        loading: budgetLoading,
        loadingKeys: budgetLoadingKeys,
        firstLoadComplete: budgetFirstLoadComplete,
        selectedBudget,
        budgetSettings,
        budgetHistory,
        budgetSource,
    } = useSelector((state) => state.budget);
    const {
        loading: forecastLoading,
        loadingKeys: forecastLoadingKeys,
        forecastOptions,
        demandPlanningOptions,
        forecastValues
    } = useSelector((state) => state.forecast);
    const {
        loading: forecastPlanningLoading,
        loadingKeys: forecastPlanningLoadingKeys,
        plannedForecastsOptions,
    } = useSelector((state) => state.forecastPlanning);
    const {
        loading: categoriesLoading,
        loadingKeys: categoriesLoadingKeys,
        categories: reportCategories
    } = useSelector((state) => state.reportCategories);
    const {
        loading: budgetLineItemLoading,
        loadingKeys: budgetLineItemLoadingKeys,
        firstLoadComplete: budgetLineItemFirstLoadComplete,
        budgetLineItemsTotals,
        budgetLineItems,
        budgetLineItemsRollups
    } = useSelector((state) => state.budgetLineItem);

    useEffect(() => {
        if (!budgetId) return;
        dispatch(getBudgetRequest({ id: budgetId }));
        dispatch(getReportCategories({}));
        dispatch(getForecastOptions({}));
        dispatch(getForecastValues({}));
        dispatch(getPlannedForecastsOptions({}));
        dispatch(getDemandPlanningOptions({}));
        dispatch(getBudgetSettings({}));
        dispatch(getBudgetSource(budgetId));
    }, [budgetId]);

    useEffect(() => {
        return () => {
            dispatch(clearBudgetLineItems({}));
            dispatch(clearSelectedBudget({}));
        }
    }, []);

    useEffect(() => {
        const selectedValue = selectedBudget?.forecast_id;
        const currentForecastOptions = arrayHasItems(forecastOptions)
            ? forecastOptions.map(opt => ({
                label: selectedValue === `${opt.value}-defaultforecast` ? `${opt.label} (legacy)` : `${opt.label} (F)`,
                value: `${opt.value}-defaultforecast`,
                forecastYear: opt.period,
            }))
            : [];
        const currentPlannedForecastsOptions = arrayHasItems(plannedForecastsOptions)
            ? plannedForecastsOptions.map(opt => ({
                label: selectedValue === `${opt.value}-plannedforecast` ? `${opt.label} (legacy)` : `${opt.label} (M)`,
                value: `${opt.value}-plannedforecast`,
                forecastYear: opt.forecast_year,
            }))
            : [];
        const currentDemandPlanningOptions = arrayHasItems(demandPlanningOptions)
            ? demandPlanningOptions.map(opt => ({
                label: opt.publish_status === 'PUBLISHED'
                    ? `${opt.label} (D)`
                    : `${opt.label} (D – Draft)`,
                value: `${opt.value}-demandplan`,
                forecastYear: opt.forecast_year,
                publishStatus: opt.publish_status,
            }))
            : [];
        const budgetYear = selectedBudget?.period;
        const options = [
            ...currentForecastOptions,
            ...currentPlannedForecastsOptions,
            ...currentDemandPlanningOptions,
        ].filter((option) => {
            const isLegacy = option.publishStatus == null;
            const isPublishedDemandPlan = option.publishStatus === 'PUBLISHED';
            const isSelectedLegacy = isLegacy && option.value === selectedValue;
            const matchesYear = option.forecastYear == null ||
                budgetYear == null ||
                String(option.forecastYear) === String(budgetYear);
            return matchesYear && (isSelectedLegacy || isPublishedDemandPlan);
        });
        setFilteredForecasts(options);
    }, [
        forecastOptions,
        plannedForecastsOptions,
        demandPlanningOptions,
        selectedBudget?.forecast_id,
        selectedBudget?.period,
    ]);

    const netIncomeTotal = budgetLineItemsTotals?.netIncome;
    const categoriesTotal = budgetLineItemsTotals?.categories;
    const getCategoryMonthlyTotals = (categoryId) => categoriesTotal?.[String(categoryId)] || null;

    useEffect(() => {
        if (!selectedBudget) return;
        if (budgetFirstLoadComplete) {
            dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id }));
            setSelectedBudgetForecast(selectedBudget?.forecast_id);
        }
    }, [
        selectedBudget
    ]);

    const monthsLong = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const closedMonths = monthsLong.map((_, index) => (
        !!getClosedBudgetPeriod(budgetSource?.periods, selectedBudget?.period, index)
    ));
    const capturedActualsByMonth = monthsLong.map((_, index) => (
        getClosedBudgetPeriod(budgetSource?.periods, selectedBudget?.period, index)?.actuals || null
    ));
    const actualsByMonth = budgetView === "forecast" ? [] : capturedActualsByMonth;
    const budgetActualsPillState = getBudgetActualsPillState(budgetSource?.periods || [], new Date());
    const actualsDividerIndex = actualsByMonth.reduce(
        (lastIndex, actuals, index) => actuals?.lines ? index : lastIndex,
        -1,
    );
    const currentBudgetLineItems = arrayHasItems(budgetLineItems) ? budgetLineItems : [];

    const getFilteredLineItems = (categoryId) => {
        if (!categoryId) return [];
        const hiddenSet = hiddenByCategoryId?.[categoryId] || new Set();
        return currentBudgetLineItems
            .filter((budgetLineItem) => budgetLineItem.categoryId === categoryId)
            .filter((budgetLineItem) => !hiddenSet.has(budgetLineItem.id));
    }

    // Get active categories from budget, filter and sort by position
    const activeCategoryIds = Array.isArray(selectedBudget?.active_categories) 
        ? selectedBudget.active_categories 
        : (selectedBudget?.active_categories ? JSON.parse(selectedBudget.active_categories) : null);
    
    const activeCategories = arrayHasItems(activeCategoryIds) && arrayHasItems(reportCategories)
        ? [...reportCategories]
            .filter(cat => activeCategoryIds.includes(cat.id))
            .sort((a, b) => {
                const posA = a.position ?? a.id;
                const posB = b.position ?? b.id;
                return posA - posB;
            })
        : [];

    const netIncomeMonthValues = Object.values(netIncomeTotal?.months || {}).map((value, index) => (
        getDisplayedNetIncomeMonthValue(value, currentBudgetLineItems, activeCategories, index, actualsByMonth)
    ));
    const netIncomeFullYearValue = netIncomeMonthValues.reduce((total, value) => total + value, 0);

    const getCategoryStyle = () => {
        return {
            bg: "",
            text: "",
            bgColor: "#f9fafb",
            textColor: "#1f2937",
        };
    };
    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const csv = buildBudgetCsv({
                year: selectedBudget?.period,
                categories: activeCategories.map((category) => ({
                    ...category,
                    items: getFilteredLineItems(category.id),
                    monthValues: Object.values(getCategoryMonthlyTotals(category.id)?.months || {}),
                })),
                actualsByMonth,
                actualsPresentByMonth: capturedActualsByMonth.map((actuals) => Boolean(actuals?.lines)),
                view: budgetView,
                closedMonths,
                netIncomeMonthValues,
            });
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'budget_report.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const transformLineItemsToTemplateFormat = () => {
        if (!arrayHasItems(budgetLineItems) || !arrayHasItems(reportCategories)) {
            return [];
        }

        // Get active categories - only include line items from active categories
        const activeCategoryIdsSet = new Set(activeCategoryIds || []);

        // Group line items by categoryId (only from active categories)
        const itemsByCategory = {};
        budgetLineItems.forEach((item) => {
            const categoryId = item.categoryId;
            if (!categoryId) return;
            
            // Only include items from active categories
            if (!activeCategoryIdsSet.has(categoryId)) return;
            
            if (!itemsByCategory[categoryId]) {
                itemsByCategory[categoryId] = [];
            }
            
            itemsByCategory[categoryId].push({
                id: item.id,
                code: item.accountNumber || '',
                name: item.accountName || '',
                type: item.type || 'EDITABLE'
            });
        });

        // Build categories array matching activeCategories (which are already filtered and sorted)
        const categories = [];
        activeCategories.forEach((category) => {
            const categoryId = category.id;
            const categoryItems = itemsByCategory[categoryId] || [];
            
            if (categoryItems.length > 0) {
                categories.push({
                    categoryId: categoryId,
                    categoryName: category.name || '',
                    children: categoryItems
                });
            }
        });

        return categories;
    };

    const handleSaveTemplate = async ({ name }) => {
        try {
            const categories = transformLineItemsToTemplateFormat();
            
            if (!arrayHasItems(categories)) {
                toast.error('No line items found to save as template.', { position: "bottom-right" });
                return;
            }

            await dispatch(createBudgetTemplate({
                name,
                items: {
                    categories
                }
            })).unwrap();

            setSaveTemplateModalIsOpen(false);
        } catch (error) {
            console.error('Failed to save template:', error);
            toast.error(error?.message || 'Failed to save template.', { position: "bottom-right" });
        }
    };


    const budgetSettingsIsLoading = () => 
        budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.getBudgetSettings);
    const getCardsIsLoading = () => 
        budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.getBudgetCards);
    const deleteBudgetLineItemIsLoading = () =>
        budgetLoading && budgetLineItemLoadingKeys.includes(BudgetLineItemLoadingKeys.deleteBudgetLineItem);
    const getBudgetLineItemsIsLoading = () =>
        budgetLineItemLoading && budgetLineItemLoadingKeys.includes(BudgetLineItemLoadingKeys.getBudgetLineItems);
    const getBudgetUpdateInputLineItemIsLoading = () => 
        budgetLineItemLoading && budgetLineItemLoadingKeys.includes(BudgetLineItemLoadingKeys.updateMonthValue);
    const forecastOptionsIsLoading = () => 
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.getForecastOptions);
    const demandPlanningOptionsIsLoading = () =>
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.getDemandPlanningOptions);
    const getPlannedForecastOptionsIsLoading = () =>
        forecastPlanningLoading && forecastPlanningLoadingKeys.includes(ForecastPlanningLoadingKeys.getPlannedForecastsOptions);

    // Keep the thead pinned while the grid moves through the page.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const applyStickyY = () => {
            const rect = container.getBoundingClientRect();
            const thead = container.querySelector('thead');
            if (!thead) return;
            const theadRect = thead.getBoundingClientRect();
            const theadHeight = theadRect.height;

            // Desired sticky offset: clamp(-rect.top, 0, rect.height - theadHeight)
            const maxY = Math.max(rect.height - theadHeight, 0);
            const unclampedY = -rect.top;
            let y = Math.min(Math.max(unclampedY, 0), maxY);

            const dpr = window.devicePixelRatio || 1;
            const snappedY = Math.round(y * dpr) / dpr;
            container.style.setProperty('--sticky-y', `${snappedY}px`);
        };

        let rafId = null;
        const scheduleUpdate = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                applyStickyY();
            });
        };

        const onWindowScroll = () => scheduleUpdate();

        // Initial apply
        scheduleUpdate();

        const onResize = () => scheduleUpdate();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onWindowScroll, { passive: true });

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onWindowScroll);
        };
    }, [budgetId, budgetLineItemFirstLoadComplete]);

    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        const table = budgetTableRef.current;
        const spacer = budgetGridSpacerRef.current;
        if (!container || !table || !spacer) return;

        const updateSpacerHeight = () => {
            spacer.style.height = "0px";
            const naturalTableHeight = table.offsetHeight;
            const nextHeight = Math.max(container.clientHeight - naturalTableHeight, 0);
            spacer.style.height = `${nextHeight}px`;
            setBudgetGridSpacerHeight((currentHeight) =>
                currentHeight === nextHeight ? currentHeight : nextHeight
            );
        };

        updateSpacerHeight();
        const onResize = () => {
            requestAnimationFrame(updateSpacerHeight);
        };
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [budgetId, budgetLineItemFirstLoadComplete, budgetLineItems, reportCategories]);

    if (!budgetId) return null;

    return (
        <>
            <main className="flex h-[100dvh] min-h-0 w-full flex-col overflow-hidden px-4 sm:px-6 lg:px-8">
                <div className="flex h-auto flex-col py-3 pb-0">
                    <div className="space-y-2 border-b border-gray-200 pb-3">
                        <div className="grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto] items-center gap-x-2 gap-y-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2 lg:flex-nowrap">
                            <button type="button" aria-label="Return to budgets" onClick={() => navigate(ROUTES.BUDGET_X_FORECAST)} className="col-start-1 row-start-1 group flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:col-auto sm:row-auto">
                                <ArrowLeftIcon className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
                            </button>
                            <span className="col-start-2 row-start-1 whitespace-nowrap text-sm font-semibold text-gray-900 sm:col-auto sm:row-auto">Budget</span>
                            {selectedBudget && (
                                <>
                                    <span className="col-start-3 row-start-1 min-w-0 max-w-full truncate text-sm font-medium text-gray-900 sm:col-auto sm:row-auto sm:max-w-[min(45vw,32rem)]" title={selectedBudget.name}>{selectedBudget.name}</span>
                                    <span className="col-start-4 row-start-1 whitespace-nowrap text-xs text-gray-500 sm:col-auto sm:row-auto">{selectedBudget.period}</span>
                                    <div className="col-start-1 col-span-6 row-start-2 flex min-w-0 items-center gap-2 sm:contents">
                                        <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-[11px] font-semibold ${selectedBudget.publish_status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {selectedBudget.publish_status === 'PUBLISHED' ? 'LIVE' : 'Draft'}
                                        </span>
                                        <div role="group" aria-label="Budget view" className="flex shrink-0 overflow-hidden rounded-md border border-gray-300">
                                            <button
                                                type="button"
                                                onClick={() => handleBudgetViewChange("actuals")}
                                                aria-pressed={budgetView === "actuals"}
                                                className={`h-7 px-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 ${
                                                    budgetView === "actuals" ? "bg-green-100 text-green-800" : "bg-white text-zinc-600"
                                                }`}
                                            >
                                                YTD Actuals
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleBudgetViewChange("forecast")}
                                                aria-pressed={budgetView === "forecast"}
                                                className={`h-7 border-l border-gray-300 px-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 ${
                                                    budgetView === "forecast" ? "bg-green-100 text-green-800" : "bg-white text-zinc-600"
                                                }`}
                                            >
                                                Forecast
                                            </button>
                                        </div>
                                        {budgetView === "actuals" && budgetSource && (
                                            <button
                                                type="button"
                                                onClick={() => setBudgetSettingsSidebarOpen(true)}
                                                title={budgetActualsPillState.title}
                                                className={`inline-flex min-w-0 max-w-[min(38vw,14rem)] shrink items-center rounded-full border px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    budgetActualsPillState.tone === 'warning'
                                                        ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                                                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                <span className="truncate">{budgetActualsPillState.label}</span>
                                            </button>
                                        )}
                                        {budgetSource?.source?.kind === 'demandplan' && (
                                            <button
                                                type="button"
                                                onClick={() => setBudgetSettingsSidebarOpen(true)}
                                                title={`${budgetSource.source.plan?.name || 'Demand Plan'} · ${budgetSource.source.plan?.publish_status === 'PUBLISHED' ? 'Published' : 'Draft'} · updated ${formatBudgetDate(budgetSource.source.plan?.updated_at)} · ${budgetSource.snapshot?.derived_at ? `synced ${formatBudgetDate(budgetSource.snapshot.derived_at)}` : 'not synced'}`}
                                                className="ml-0 inline-flex max-w-full min-w-0 flex-1 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex-none sm:ml-auto"
                                            >
                                                <span className="min-w-0 max-w-[32vw] truncate font-medium sm:max-w-[min(32vw,24rem)]">Demand Plan: {budgetSource.source.plan?.name || 'Demand Plan'}</span>
                                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                    !budgetSource.snapshot
                                                        ? 'bg-gray-100 text-gray-600'
                                                        : (budgetSource.material === true || budgetSource.material === null)
                                                            ? 'bg-amber-100 text-amber-800'
                                                            : 'bg-emerald-50 text-emerald-700'
                                                }`}>
                                                    {!budgetSource.snapshot ? 'Not synced' : (budgetSource.material === true || budgetSource.material === null) ? 'New Demand Plan' : 'Current'}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        aria-label="Budget Settings"
                                        title="Budget Settings"
                                        onClick={() => setBudgetSettingsSidebarOpen(true)}
                                        className="col-start-5 row-start-1 flex h-9 w-9 shrink-0 items-center justify-center rounded border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:col-auto sm:row-auto"
                                    >
                                        <Cog8ToothIcon className="h-4 w-4" />
                                    </button>
                                    <Menu as="div" className="relative col-start-6 row-start-1 shrink-0 justify-self-end sm:col-auto sm:row-auto">
                                        <MenuButton type="button" aria-label="More budget actions" className="flex h-9 w-9 items-center justify-center rounded border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                            <EllipsisVerticalIcon className="h-5 w-5" />
                                        </MenuButton>
                                        <MenuItems className="absolute right-0 z-50 mt-1 w-56 max-w-[calc(100vw_-_2rem)] origin-top-right rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                                            {access === 'edit' && <MenuItem as="button" type="button" onClick={() => setBudgetModalIsOpen(true)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Edit budget</MenuItem>}
                                            {access === 'edit' && selectedBudget.publish_status !== 'PUBLISHED' && <MenuItem as="button" type="button" onClick={() => { setBudgetUnpublish(false); setBudgetPublishModalOpen(true); }} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Publish Budget</MenuItem>}
                                            <MenuItem as="button" type="button" onClick={() => setBudgetSettingsSidebarOpen(true)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Budget Settings</MenuItem>
                                            <MenuItem as="button" type="button" onClick={() => setHistoryIsOpen(true)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Budget History</MenuItem>
                                            <MenuItem as="button" type="button" onClick={() => setSaveTemplateModalIsOpen(true)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Save as Template</MenuItem>
                                            {budgetSheetImportAccess && <MenuItem as="button" type="button" onClick={() => setBudgetSheetImportOpen(true)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Import Google Sheet</MenuItem>}
                                            <MenuItem as="button" type="button" onClick={() => navigate(`${ROUTES.PROFIT_AND_LOSS}?compareType=budget-${selectedBudget.id}`)} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Budget vs Actual report</MenuItem>
                                            <MenuItem as="button" type="button" onClick={handleExport} className="block w-full rounded px-3 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100">Export data as CSV</MenuItem>
                                            {access === 'edit' && selectedBudget.publish_status === 'PUBLISHED' && <MenuItem as="button" type="button" onClick={() => { setBudgetUnpublish(true); setBudgetPublishModalOpen(true); }} className="block w-full rounded px-3 py-2 text-left text-sm text-red-700 data-[focus]:bg-red-50">Unpublish</MenuItem>}
                                        </MenuItems>
                                    </Menu>
                                </>
                            )}
                        </div>


                    </div>

                </div>
                {!budgetFirstLoadComplete ? (
                    <LoaderSpinner width="70" height="70" border="5" />
                ) : (
                    <section className="flex min-h-0 flex-1 flex-col">
                        {!budgetLineItemFirstLoadComplete ? (
                            <div className="my-6">
                                <LoaderSpinner width="60" height="60" border="5" />
                            </div>
                        ) : (
                            <div className="budget-grid-scroll relative block h-full min-h-0 w-full max-w-full overflow-auto rounded-md border border-gray-200" ref={scrollContainerRef}>
                                {/* {getBudgetLineItemsIsLoading() && <OverlayGrayBackground />} */}
                                <table ref={budgetTableRef} className="w-full border-separate border-spacing-0 text-[13px]">
                                    <thead className="sticky top-0 z-20">
                                        <tr className="transition-colors border-b border-gray-200">
                                            <td
                                                data-sticky-col="1"
                                                className="whitespace-nowrap bg-gray-50 px-2 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-gray-500"
                                                style={{
                                                    left: 0,
                                                    position: "sticky",
                                                    transform: "translate3d(0, var(--sticky-y, 0px), 0)",
                                                    zIndex: 4,
                                                }}
                                            >
                                                COA
                                            </td>
                                            <td
                                                data-sticky-col="2"
                                                className="whitespace-nowrap bg-gray-50 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-gray-500"
                                                style={{
                                                    left: "var(--budget-grid-coa-width)",
                                                    position: "sticky",
                                                    transform: "translate3d(0, var(--sticky-y, 0px), 0)",
                                                    zIndex: 4,
                                                    boxShadow: "2px 0 0 0 rgba(0,0,0,0.06)",
                                                }}
                                            >
                                                Account Name
                                            </td>
                                            {monthsLong.map((month, index) => {
                                                return (
                                                    <td
                                                        className={`min-w-[96px] whitespace-nowrap bg-gray-50 px-1 py-1.5 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500 ${index === actualsDividerIndex ? 'border-r-2 border-indigo-200' : ''}`}
                                                        key={`${month}_${index}`}
                                                        data-month-index={index}
                                                        style={{
                                                            transform: "translate3d(0, var(--sticky-y, 0px), 0)",
                                                            willChange: "transform",
                                                            backfaceVisibility: "hidden",
                                                            contain: "paint",
                                                            position: "relative",
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <span>{monthsOptions[index]?.label.toUpperCase()} {selectedBudget?.period}</span>
                                                            <BudgetMonthStatusBadge
                                                                periods={budgetSource?.periods}
                                                                year={selectedBudget?.period}
                                                                monthIndex={index}
                                                                view={budgetView}
                                                            />
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                            <td
                                                className="min-w-[140px] whitespace-nowrap bg-gray-50 px-3 py-1.5 text-right text-[12px] font-semibold uppercase tracking-wide text-gray-500"
                                                style={{
                                                    transform: "translate3d(0, var(--sticky-y, 0px), 0)",
                                                    willChange: "transform",
                                                    backfaceVisibility: "hidden",
                                                    contain: "paint",
                                                    position: "relative",
                                                    zIndex: 2,
                                                }}
                                            >
                                                Total
                                            </td>
                                            <td
                                                className="w-16 whitespace-nowrap bg-gray-50 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-gray-500"
                                                style={{
                                                    transform: "translate3d(0, var(--sticky-y, 0px), 0)",
                                                    willChange: "transform",
                                                    backfaceVisibility: "hidden",
                                                    contain: "paint",
                                                    position: "relative",
                                                    zIndex: 2,
                                                }}
                                            >
                                                {access === "edit" && ("Action")}
                                            </td>
                                        </tr>
                                    </thead>
                                    <DndProvider backend={HTML5Backend}>
                                        <tbody>
                                            {activeCategories.map((category) => {
                                                const categoryTotals = getCategoryMonthlyTotals(category.id);
                                                const categoryItems = getFilteredLineItems(category.id);
                                                const categoryMonthValues = Object.values(categoryTotals?.months || {}).map((value, index) => (
                                                    getDisplayedCategoryMonthValue(value, categoryItems, index, actualsByMonth)
                                                ));
                                                const categoryFullYearValue = categoryMonthValues.reduce((total, value) => total + value, 0);
                                                const categoryStyle = getCategoryStyle(category.id);
                                                const hasCategoryTotals = Object.values(categoryTotals?.months || {}).length > 0;
                                                return (
                                                    <React.Fragment key={category.id}>
                                                        {/* Category Header */}
                                                        <tr 
                                                            className="group border-b border-gray-200 transition-colors hover:bg-gray-50"
                                                            style={{ backgroundColor: categoryStyle.bgColor }}
                                                        >
                                                            <td
                                                                data-sticky-col="category"
                                                                className="align-middle px-3 py-1.5 text-[13px] font-semibold uppercase tracking-wide"
                                                                colSpan={Object.values(categoryTotals?.months || {}).length > 0 ? 2 : 16}
                                                                style={{
                                                                    ...(hasCategoryTotals ? {
                                                                        left: 0,
                                                                        position: "sticky",
                                                                    } : {
                                                                        position: "relative",
                                                                    }),
                                                                    zIndex: 3,
                                                                    backgroundColor: categoryStyle.bgColor,
                                                                    color: categoryStyle.textColor,
                                                                }}
                                                            >
                                                                <div className={hasCategoryTotals ? undefined : "sticky left-0 block w-max"}>
                                                                    {category.name.toUpperCase()}
                                                                </div>
                                                            </td>
                                                            {categoryMonthValues.map((value, index) => (
                                                                <td
                                                                    className="min-w-[96px] whitespace-nowrap align-middle px-1 py-1.5 text-right tabular-nums"
                                                                    key={`${index}_${value}`}
                                                                    style={{ backgroundColor: categoryStyle.bgColor }}
                                                                >
                                                                    <div className={`text-[13px] font-semibold tabular-nums ${amountTextClass(value)}`}>
                                                                        {formatBudgetAmount(value)}
                                                                    </div>
                                                                </td>
                                                            ))}
                                                            {categoryTotals?.totalByLineItems !== undefined && (
                                                                <td 
                                                                    className="min-w-[96px] whitespace-nowrap align-middle px-1 py-1.5 text-right tabular-nums"
                                                                    style={{ backgroundColor: categoryStyle.bgColor }}
                                                                >
                                                                    <div className={`text-[13px] font-semibold tabular-nums ${amountTextClass(categoryMonthValues.length ? categoryFullYearValue : categoryTotals?.totalByLineItems)}`}>
                                                                        {formatBudgetAmount(categoryMonthValues.length ? categoryFullYearValue : categoryTotals?.totalByLineItems)}
                                                                    </div>
                                                                </td>
                                                            )}
                                                            {arrayHasItems(Object.values(categoryTotals?.months || {})) && (
                                                                <td 
                                                                    className="px-1 py-1.5"
                                                                    style={{ backgroundColor: categoryStyle.bgColor }}
                                                                /> 
                                                            )}
                                                        </tr>
                                                        <LineItemsByCategory
                                                            items={categoryItems}
                                                            access={access}
                                                            categoryId={category.id}
                                                            resetVersion={listResetVersion}
                                                            onEditFormulaClick={({ budgetLineItemId, hasFormula, formula }) => {
                                                                setBudgetLineItemFormulaData({
                                                                    id: selectedBudget?.id,
                                                                    budgetLineItemId,
                                                                    formula,
                                                                    hasFormula
                                                                })
                                                            }}
                                                            inputIsLoading={getBudgetUpdateInputLineItemIsLoading() || getBudgetLineItemsIsLoading()}
                                                            onEditLineItemClick={({ budgetLineItemId, type, forecastValue, categoryId, selectedRPItems, code, name }) => {
                                                                setBudgetLineItemEditData({
                                                                    budgetLineItemId, 
                                                                    type, 
                                                                    forecastValue, 
                                                                    categoryId, 
                                                                    selectedRPItems,
                                                                    code,
                                                                    name
                                                                })
                                                            }}  
                                                            onDeleteClick={(id) => {
                                                                setBudgetIdToDelete(id);
                                                                setDeleteBudgetLineItemIsOpen(true);
                                                            }}
                                                            selectedBudget={selectedBudget}
                                                            sourceAccounts={currentBudgetLineItems}
                                                            closedMonths={closedMonths}
                                                            actualsByMonth={actualsByMonth}
                                                            demandPlanLinked={budgetSource?.source?.kind === 'demandplan'}
                                                            demandPlanName={budgetSource?.source?.plan?.name || 'Demand Plan'}
                                                        />
                                                        {access === "edit" && (
                                                            <AddBudgetLineItemButton
                                                                onClick={() => {
                                                                    setBudgetLineItemData({
                                                                        categoryId: category.id
                                                                    })
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            <tr aria-hidden="true">
                                                <td
                                                    ref={budgetGridSpacerRef}
                                                    colSpan={2 + 12 + 1 + (access === "edit" ? 1 : 0)}
                                                    className="p-0"
                                                    style={{ height: `${budgetGridSpacerHeight}px` }}
                                                />
                                            </tr>
                                            {/* Total NetIcome */}
                                            <tr className="sticky bottom-0 z-20 border-t border-gray-700 bg-gray-900 text-white">
                                                <td
                                                    data-sticky-col="1"
                                                    className="align-middle bg-gray-900 px-2 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-white"
                                                    style={{
                                                        left: 0,
                                                        position: "sticky",
                                                        zIndex: 4,
                                                    }}
                                                >
                                                    <span>NET</span>
                                                </td>
                                                <td
                                                    data-sticky-col="2"
                                                    className="align-middle bg-gray-900 px-3 py-1.5 text-[13px] font-semibold text-white"
                                                    style={{
                                                        left: "var(--budget-grid-coa-width)",
                                                        position: "sticky",
                                                        zIndex: 4,
                                                        boxShadow: "2px 0 0 0 rgba(255,255,255,0.12)",
                                                    }}
                                                >
                                                    Net Income
                                                </td>
                                                {netIncomeMonthValues.map((value, index) => (
                                                    <td
                                                        className="min-w-[96px] whitespace-nowrap align-middle bg-gray-900 px-1 py-1.5 text-right tabular-nums"
                                                        key={`${index}_${value}`}
                                                    >
                                                        <div className={`text-[13px] font-semibold tabular-nums ${amountTextClass(value, "text-white")}`}>
                                                            {formatBudgetAmount(value)}
                                                        </div>
                                                    </td>
                                                ))}
                                                {netIncomeTotal?.total !== undefined && (
                                                    <td className="min-w-[140px] whitespace-nowrap align-middle bg-gray-900 px-1 py-1.5 text-right tabular-nums">
                                                        <div className={`text-[13px] font-semibold tabular-nums ${amountTextClass(netIncomeMonthValues.length ? netIncomeFullYearValue : netIncomeTotal?.total, "text-white")}`}>
                                                            {formatBudgetAmount(netIncomeMonthValues.length ? netIncomeFullYearValue : netIncomeTotal?.total)}
                                                        </div>
                                                    </td>
                                                )}
                                            <td className="w-16 align-middle bg-gray-900 px-3 py-1.5 text-center" />
                                            </tr>
                                        </tbody>
                                    </DndProvider>
                                </table>
                            </div>
                        )}
                    </section>
                )}
            </main>
            <BudgetModal
                key={`${selectedBudget?.name}_${selectedBudget?.period}_${selectedBudget?.forecast_id}`}
                isOpen={access === "edit" && budgetModalIsOpen}
                onClose={() => {
                    setBudgetModalIsOpen(false);
                }}
                labelItem={selectedBudget?.name}
                periodItem={selectedBudget?.period}
                netIncomeOptions={(budgetSettings?.netIncomeCalculations || []).map((netIncome) => {
                    return {
                        label: netIncome?.name,
                        value: netIncome?.id
                    }
                })}
                netIncomeOptionsLoading={budgetSettingsIsLoading()}
                selectedNetIncomeItem={selectedBudget?.net_income_calculation_id}
                activeCategories={selectedBudget?.active_categories}
                reportCategories={reportCategories}
                forecastOptionsLoading={forecastOptionsIsLoading() || demandPlanningOptionsIsLoading() || getPlannedForecastOptionsIsLoading()}
                selectedForecastItem={selectedBudget?.forecast_id}
                forecastOptions={filteredForecasts}
                onAccept={async ({ name, period, selectedForecast, activeCategories, selectedNetIncome }) => {
                    await dispatch(createBudgetRequest({ id: selectedBudget?.id || null, name, period, selectedForecast, activeCategories, selectedNetIncome }));
                    setBudgetModalIsOpen(false);
                    dispatch(getBudgetRequest({ id: budgetId }));
                }}
            />
            <BudgetLineItemModal
                isOpen={!!budgetLineItemData && access === "edit"}
                {...(budgetLineItemData || {})}
                options={(reportCategories || [])
                    .filter(category => !arrayHasItems(activeCategoryIds) || activeCategoryIds.includes(category.id))
                    .map((category) => {
                        return {
                            label: category?.name, value: category?.id
                        }
                })}
                onAccept={async ({ accountName, accountNumber, selectedType, selectedCategory, defaultMonthValue }) => {
                    await dispatch(upsertBudgetLineItemRequest({
                        accountName, accountNumber, selectedType, selectedCategory, defaultMonthValue, budgetReportId: selectedBudget?.id
                    })).unwrap();
                    await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
                    setBudgetLineItemData(null);
                }}
                onClose={() => {
                    setBudgetLineItemData(null);
                }}
            />
            <BudgetLineItemEditModal
                key={`${budgetLineItemEditData?.budgetLineItemId}_${selectedBudget?.id}_edit`}
                isOpen={!!budgetLineItemEditData && access === "edit"}
                {...(budgetLineItemEditData || {})}
                options={(reportCategories || [])
                    .filter(category => !arrayHasItems(activeCategoryIds) || activeCategoryIds.includes(category.id))
                    .map((category) => {
                        return {
                            label: category?.name, value: category?.id
                        }
                })}
                rollupItems={budgetLineItemsRollups || []}
                selectedForecast={selectedBudgetForecast}
                forecastValues={[
                    { label: "Shopify", value: "Shopify" },
                    { label: "Amazon", value: "Amazon" },
                    { label: "Wholesale", value: "Wholesale" },
                ]}
                onAccept={async ({ 
                    budgetLineItemId,
                    code,
                    name,
                    selectedType,
                    selectedCategory, 
                    selectedForecastValue,
                    selectedRollUpItems
                }) => {
                    await dispatch(updateBudgetLineItemRequest({
                        selectedType, 
                        selectedForecast: selectedBudgetForecast, 
                        selectedForecastValue, 
                        selectedCategory, 
                        budgetLineItemId, 
                        selectedRollUpItems,
                        code,
                        name
                    })).unwrap();
                    await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
                    setBudgetLineItemEditData(null);
                }}
                onDelete={async () => {
                    if (budgetLineItemEditData?.budgetLineItemId) {
                        await dispatch(deleteBudgetLineItemRequest({ id: budgetLineItemEditData.budgetLineItemId })).unwrap();
                        await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
                        setBudgetLineItemEditData(null);
                    }
                }}
                isDeleteLoading={deleteBudgetLineItemIsLoading()}
                onClose={() => {
                    setBudgetLineItemEditData(null);
                }}
            />
            <BudgetLineItemFormulaModal
                key={`${budgetLineItemFormulaData?.budgetLineItemId}_${selectedBudget?.id}_formula`}
                isOpen={!!budgetLineItemFormulaData && access === "edit"}
                {...(budgetLineItemFormulaData || {})}
                onAccept={async ({ 
                    selectedOperation,
                    selectedSourceAccount,
                    currentValue,
                    isPercentageValue,
                    isSeparateValueByMonths,
                    percentageValueByMonths
                }) => {
                    await dispatch(upsertBudgetLineItemFormulaRequest({
                        id: budgetLineItemFormulaData.budgetLineItemId,
                        budgetId: selectedBudget?.id,
                        selectedOperation,
                        selectedSourceAccount,
                        currentValue,
                        isPercentageValue,
                        isSeparateValueByMonths,
                        percentageValueByMonths
                    })).unwrap();
                    await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
                    setBudgetLineItemData(null);
                    setBudgetLineItemFormulaData(null);
                }}
                onRemove={async () => {
                    await dispatch(upsertBudgetLineItemFormulaRequest({
                        id: budgetLineItemFormulaData.budgetLineItemId,
                        budgetId: selectedBudget?.id,
                        isRemove: true
                    })).unwrap();
                    await dispatch(getBudgetLineItemsCards({ id: selectedBudget?.id })).unwrap();
                    setBudgetLineItemData(null);
                    setBudgetLineItemFormulaData(null);
                }}
                onClose={() => {
                    setBudgetLineItemFormulaData(null);
                }}
            />
            <ConfrimModal
                isOpen={(deleteBudgetLineItemIsOpen) && access === "edit"}
                title={
                    `Are you sure want to delete this budget line item?`
                }
                isLoading={deleteBudgetLineItemIsLoading()}
                confirmButtonTitle={"Delete"}
                submitClick={async () => {
                    if (deleteBudgetLineItemIsOpen) {
                        await dispatch(deleteBudgetLineItemRequest({ id: budgetIdToDelete })).unwrap();
                    }
                    setBudgetIdToDelete("");
                    setDeleteBudgetLineItemIsOpen(false);
                    await dispatch(getBudgetLineItemsCards({ id: budgetId })).unwrap();
                }}
                cancelClick={() => {
                    setBudgetIdToDelete("");
                    setDeleteBudgetLineItemIsOpen(false);
                }}
            />
            <BudgetHistorySidebar
                isOpen={historyIsOpen}
                budgetId={budgetId}
                onClose={() => {
                    setHistoryIsOpen(false);
                }}
            />
            <SaveBudgetTemplateModal
                isOpen={saveTemplateModalIsOpen}
                onClose={() => {
                    setSaveTemplateModalIsOpen(false);
                }}
                onAccept={handleSaveTemplate}
                isLoading={budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.createTemplate)}
            />
            <BudgetSettingsSidebar
                isOpen={budgetSettingsSidebarOpen}
                budgetId={budgetId}
                openPanel={budgetSettingsPanel}
                onPanelOpened={() => setBudgetSettingsPanel(null)}
                onClose={() => setBudgetSettingsSidebarOpen(false)}
                onReviewChanges={() => {
                    setBudgetSettingsSidebarOpen(false);
                    setBudgetRefreshModalOpen(true);
                }}
                onCategoryChange={async () => {
                    await dispatch(getReportCategories({}));
                    if (budgetId) {
                        await dispatch(getBudgetRequest({ id: budgetId }));
                    }
                }}
            />
            <BudgetRefreshModal
                budgetId={budgetId}
                isOpen={budgetRefreshModalOpen}
                demandPlanLinked={budgetSource?.source?.kind === 'demandplan'}
                onClose={() => setBudgetRefreshModalOpen(false)}
                onSuccess={async () => {
                    await dispatch(getBudgetRequest({ id: budgetId }));
                    await dispatch(getBudgetSource(budgetId));
                }}
            />
            <BudgetSheetImportModal
                budgetId={budgetId}
                isOpen={budgetSheetImportOpen}
                canEdit={budgetSheetImportAccess === 'edit'}
                onClose={() => setBudgetSheetImportOpen(false)}
                onApplied={async (result) => {
                    await dispatch(getBudgetRequest({ id: budgetId }));
                    await dispatch(getBudgetLineItemsCards({ id: budgetId }));
                    if (result?.refresh?.ran) {
                        await dispatch(getBudgetSource(budgetId));
                    }
                }}
            />
            <BudgetPublishModal
                budgetId={budgetId}
                isOpen={budgetPublishModalOpen}
                unpublish={budgetUnpublish}
                onClose={() => setBudgetPublishModalOpen(false)}
                onSuccess={async () => {
                    await dispatch(getBudgetRequest({ id: budgetId }));
                    await dispatch(getBudgetSource(budgetId));
                }}
            />
        </>
    )
}

export default Budget;